import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { Check, Loader2, Plus, Trash2 } from "lucide-react";
import { IntegrationLogo } from "@/components/integrations/logo";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  credentialsApi,
  type Credential,
} from "@/lib/credentials-api";
import { integrationsApi, type IntegrationCatalogRow } from "@/lib/integrations-api";
import {
  ALL_AUTH_KINDS,
  AUTH_STRATEGY_INFO,
  authLabel,
  type AuthStrategyKind,
} from "@memkernel/integration-kit";
import type { CredentialKind } from "@/lib/credentials-api";
import {
  AddCredentialDialog,
  type AddCredentialOption,
} from "./AddCredentialDialog";

// What the connect dialog accepts. Anything the kit's registry rules
// out — `none`, `session`, `iam`, and the catalog-only `builtin` —
// stays out. Computed once at module init.
const CONNECTABLE_KINDS: ReadonlySet<AuthStrategyKind> = new Set(
  ALL_AUTH_KINDS.filter(
    (k) => AUTH_STRATEGY_INFO[k].connectMode !== "framework_managed",
  ),
);

// Stable section ordering for the picker. Derived from the kit's
// declaration order — adding a kind to the kit puts it after the
// existing kinds without us editing this file.
const KIND_ORDER: ReadonlyMap<AuthStrategyKind, number> = new Map(
  ALL_AUTH_KINDS.map((k, i) => [k, i]),
);

function groupCatalog(integrations: IntegrationCatalogRow[]): AddCredentialOption[] {
  // One entry per (integration, auth method) pair. Integrations sharing an
  // upstream OAuth provider (e.g. Gmail + Google Calendar) appear as
  // SEPARATE entries — connecting one does not grant the other; each
  // gets its own consent screen with its own scopes.
  const out: AddCredentialOption[] = [];
  for (const t of integrations) {
    for (const method of t.auth) {
      // Catalog rows include `builtin`, which isn't a real strategy
      // kind — and `none`/`session`/`iam` are framework-managed, not
      // user-connectable.
      if (!isStrategyKind(method.kind) || !CONNECTABLE_KINDS.has(method.kind)) {
        continue;
      }
      out.push({
        id: `${method.kind}:${t.slug}`,
        storageKey: t.slug,
        kind: method.kind,
        name: t.name,
        integrations: [t],
        driverIntegration: t,
        logoSlug: t.slug,
        logoUrl: t.logo,
        // Pretty-printed upstream provider, used to render
        // "Connect with <Provider>" copy. Only OAuth strategies carry
        // the provider field; the rest fall back to the integration name.
        providerLabel:
          method.kind === "oauth2" || method.kind === "oauth2_built_in"
            ? prettyProvider(method.provider)
            : undefined,
      });
    }
  }
  return out.sort(
    (a, b) =>
      (KIND_ORDER.get(a.kind) ?? 0) - (KIND_ORDER.get(b.kind) ?? 0) ||
      a.name.localeCompare(b.name),
  );
}

function isStrategyKind(kind: string): kind is AuthStrategyKind {
  return kind in AUTH_STRATEGY_INFO;
}

// "google" → "Google", "github" → "GitHub". Falls back to the raw
// provider key when the prettifier has nothing better.
function prettyProvider(p: string | undefined): string | undefined {
  if (!p) return undefined;
  const known: Record<string, string> = {
    google: "Google",
    github: "GitHub",
    slack: "Slack",
    notion: "Notion",
    microsoft: "Microsoft",
    atlassian: "Atlassian",
  };
  return known[p] ?? p[0]!.toUpperCase() + p.slice(1);
}

// Stored credentials can carry the legacy `"oauth"` marker and the
// `"username_password"` alias for `"basic"`; everything else maps onto
// the kit's strategy registry, so a new strategy kind shows up here
// with the right label automatically.
function credentialKindLabel(kind: CredentialKind): string {
  if (kind === "oauth") return "OAuth 2.0";
  if (kind === "username_password") return authLabel("basic");
  return authLabel(kind);
}

function relativeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  return `${days}d ago`;
}

export function CredentialsPage() {
  const [params] = useSearchParams();
  const justConnected = params.get("connected");
  const [addOpen, setAddOpen] = useState(false);

  const { data: catalog } = useQuery({
    queryKey: ["integrations"],
    queryFn: integrationsApi.list,
    staleTime: 60_000,
  });
  const { data: creds, isPending: credsPending } = useQuery({
    queryKey: ["credentials"],
    queryFn: () => credentialsApi.list(),
  });

  const options = useMemo(
    () => (catalog ? groupCatalog(catalog) : []),
    [catalog],
  );
  const optionByStorageKey = useMemo(() => {
    const map = new Map<string, AddCredentialOption>();
    for (const o of options) {
      if (!map.has(o.storageKey)) map.set(o.storageKey, o);
    }
    return map;
  }, [options]);

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Credentials</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Connect your upstream accounts. Each integration has its own
            credential — connecting Gmail doesn't grant Calendar, so scopes
            and revocations stay narrow. Bind one inside a loadout to give
            an agent access.
          </p>
        </div>
        <Button onClick={() => setAddOpen(true)}>
          <Plus className="mr-2 h-4 w-4" /> Add credential
        </Button>
      </div>

      {justConnected && (
        <div className="flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-500">
          <Check className="h-4 w-4" />
          {justConnected} account connected.
        </div>
      )}

      {credsPending || !catalog ? (
        <div className="flex h-40 items-center justify-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (creds?.length ?? 0) === 0 ? (
        <EmptyState onAdd={() => setAddOpen(true)} />
      ) : (
        <CredentialsTable
          credentials={creds ?? []}
          optionByStorageKey={optionByStorageKey}
        />
      )}

      <AddCredentialDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        options={options}
      />
    </div>
  );
}

// ── Table ──────────────────────────────────────────────────────────────
function CredentialsTable({
  credentials,
  optionByStorageKey,
}: {
  credentials: Credential[];
  optionByStorageKey: Map<string, AddCredentialOption>;
}) {
  const queryClient = useQueryClient();
  const disconnect = useMutation({
    mutationFn: (id: string) => credentialsApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["credentials"] });
    },
  });

  return (
    <div className="overflow-hidden rounded-xl border bg-card">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/40 hover:bg-muted/40">
            <TableHead className="w-[300px]">Account</TableHead>
            <TableHead>Integration</TableHead>
            <TableHead className="hidden md:table-cell">Type</TableHead>
            <TableHead className="w-[140px]">Added</TableHead>
            <TableHead className="w-[60px] text-right" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {credentials.map((c) => {
            const option = optionByStorageKey.get(c.integration);
            const logoSlug = option?.logoSlug ?? c.integration;
            const logoUrl = option?.logoUrl;
            const name = option?.name ?? c.integration;
            return (
              <TableRow key={c.id}>
                <TableCell>
                  <div className="flex min-w-0 items-center gap-3">
                    <IntegrationLogo
                      slug={logoSlug}
                      src={logoUrl}
                      name={name}
                      size={28}
                    />
                    <div className="min-w-0">
                      <div className="truncate font-medium">{c.label}</div>
                      {c.scopes.length > 0 && (
                        <div className="truncate text-xs text-muted-foreground">
                          {c.scopes.length} scope
                          {c.scopes.length === 1 ? "" : "s"} granted
                        </div>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell>{name}</TableCell>
                <TableCell className="hidden md:table-cell">
                  <Badge variant="outline" className="font-normal">
                    {credentialKindLabel(c.kind)}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {relativeAgo(c.createdAt)}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => disconnect.mutate(c.id)}
                    disabled={disconnect.isPending}
                    aria-label={`Disconnect ${c.label}`}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

// ── Empty state ────────────────────────────────────────────────────────
function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="flex flex-col items-center gap-4 rounded-xl border border-dashed bg-muted/20 p-12 text-center">
      <h2 className="text-base font-medium">No credentials yet</h2>
      <p className="max-w-md text-sm text-muted-foreground">
        Connect a provider to start binding tools to agents. OAuth pops a
        browser window; API keys land in a small form.
      </p>
      <Button onClick={onAdd}>
        <Plus className="mr-2 h-4 w-4" /> Add credential
      </Button>
    </div>
  );
}
